// import { useCallback, useEffect, useRef } from "react";
// import { Id, toast, ToastContent, ToastOptions } from "react-toastify";

// /**
//  * Hook que garante:
//  * - Disparo no container da página (containerId).
//  * - Proteção contra duplicados (opcional via toastId).
//  * - Dismiss automático dos toasts desta página no unmount (opcional).
//  */
// export function usePageToast(
//   containerId: string,
//   autoDismissOnUnmount = false
// ) {
//   const idsRef = useRef<Id[]>([]);

//   const pushId = (id: Id) => {
//     if (!idsRef.current.includes(id)) idsRef.current.push(id);
//   };

//   const notify = useCallback(
//     (
//       content: ToastContent,
//       opts?: ToastOptions & { toastId?: string | number }
//     ) => {
//       const base: ToastOptions = { containerId, ...opts };
//       if (opts?.toastId && toast.isActive(opts.toastId)) {
//         // já existe um toast com este id; evita duplicar
//         return opts.toastId as Id;
//       }
//       const id = toast(content, base);
//       pushId(id);
//       return id;
//     },
//     [containerId]
//   );

//   const toastSuccess = useCallback(
//     (
//       content: ToastContent,
//       opts?: ToastOptions & { toastId?: string | number }
//     ) => {
//       const base: ToastOptions = { containerId, ...opts };
//       if (opts?.toastId && toast.isActive(opts.toastId))
//         return opts.toastId as Id;
//       const id = toast.success(content, base);
//       pushId(id);
//       return id;
//     },
//     [containerId]
//   );

//   const toastError = useCallback(
//     (
//       content: ToastContent,
//       opts?: ToastOptions & { toastId?: string | number }
//     ) => {
//       const base: ToastOptions = { containerId, ...opts };
//       if (opts?.toastId && toast.isActive(opts.toastId))
//         return opts.toastId as Id;
//       const id = toast.error(content, base);
//       pushId(id);
//       return id;
//     },
//     [containerId]
//   );

//   const toastInfo = useCallback(
//     (
//       content: ToastContent,
//       opts?: ToastOptions & { toastId?: string | number }
//     ) => {
//       const base: ToastOptions = { containerId, ...opts };
//       if (opts?.toastId && toast.isActive(opts.toastId))
//         return opts.toastId as Id;
//       const id = toast.info(content, base);
//       pushId(id);
//       return id;
//     },
//     [containerId]
//   );

//   const toastWarn = useCallback(
//     (
//       content: ToastContent,
//       opts?: ToastOptions & { toastId?: string | number }
//     ) => {
//       const base: ToastOptions = { containerId, ...opts };
//       if (opts?.toastId && toast.isActive(opts.toastId))
//         return opts.toastId as Id;
//       const id = toast.warn(content, base);
//       pushId(id);
//       return id;
//     },
//     [containerId]
//   );

//   const dismissAll = useCallback(() => {
//     // Descarta apenas os toasts que este hook abriu
//     idsRef.current.forEach((id) => toast.dismiss(id));
//     idsRef.current = [];
//   }, []);

//   useEffect(() => {
//     return () => {
//       if (autoDismissOnUnmount) dismissAll();
//     };
//   }, [autoDismissOnUnmount, dismissAll]);

//   return { notify, toastSuccess, toastError, toastInfo, toastWarn, dismissAll };
// }

import { useCallback, useEffect, useRef } from "react";
import { Id, toast, ToastContent, ToastOptions } from "react-toastify";

/** Id aleatório (sem depender de libs externas) */
function uid() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Hash simples p/ chaves curtas (djb2) */
function hashKey(input: string) {
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = (h * 33) ^ input.charCodeAt(i);
  return `k_${(h >>> 0).toString(36)}`;
}

/**
 * Hook de toast por página (containerId).
 * - *auto* => gera toastId automaticamente (paralelos sem esforço).
 * - *once* => deduplica por "key" (atualiza o mesmo toast).
 * - dismissAll => fecha os toasts criados por este hook (limpa no unmount se ativado).
 */
export function usePageToast(
  containerId: string,
  autoDismissOnUnmount = false
) {
  const createdIdsRef = useRef<Id[]>([]);
  const keyMapRef = useRef<Map<string, Id>>(new Map()); // key -> toastId

  const pushId = (id: Id) => {
    if (!createdIdsRef.current.includes(id)) createdIdsRef.current.push(id);
  };

  const base = (opts?: ToastOptions) => ({ containerId, ...opts });

  /** ---------- AUTO: ids únicos automáticos (vários toasts em paralelo) ---------- */
  const auto = useCallback(
    (content: ToastContent, opts?: ToastOptions) => {
      const id = toast(content, base({ ...opts, toastId: uid() }));
      pushId(id);
      return id;
    },
    [containerId]
  );
  const autoSuccess = useCallback(
    (content: ToastContent, opts?: ToastOptions) => {
      const id = toast.success(content, base({ ...opts, toastId: uid() }));
      pushId(id);
      return id;
    },
    [containerId]
  );
  const autoError = useCallback(
    (content: ToastContent, opts?: ToastOptions) => {
      const id = toast.error(content, base({ ...opts, toastId: uid() }));
      pushId(id);
      return id;
    },
    [containerId]
  );
  const autoInfo = useCallback(
    (content: ToastContent, opts?: ToastOptions) => {
      const id = toast.info(content, base({ ...opts, toastId: uid() }));
      pushId(id);
      return id;
    },
    [containerId]
  );
  const autoWarn = useCallback(
    (content: ToastContent, opts?: ToastOptions) => {
      const id = toast.warn(content, base({ ...opts, toastId: uid() }));
      pushId(id);
      return id;
    },
    [containerId]
  );

  /** ---------- ONCE: deduplicado por chave (atualiza o mesmo toast) ---------- */
  const once = useCallback(
    (key: string, content: ToastContent, opts?: ToastOptions) => {
      const k = hashKey(key);
      const existing = keyMapRef.current.get(k);
      if (existing && toast.isActive(existing)) {
        // atualiza o mesmo toast mantendo o id
        toast.update(existing, {
          render: content,
          ...base(opts)
        });
        return existing;
      }
      const id = toast(content, base({ ...opts, toastId: uid() }));
      keyMapRef.current.set(k, id);
      pushId(id);
      return id;
    },
    [containerId]
  );
  const onceSuccess = useCallback(
    (key: string, content: ToastContent, opts?: ToastOptions) =>
      once(key, content, { ...opts, type: "success" }),
    [once]
  );
  const onceError = useCallback(
    (key: string, content: ToastContent, opts?: ToastOptions) =>
      once(key, content, { ...opts, type: "error" }),
    [once]
  );
  const onceInfo = useCallback(
    (key: string, content: ToastContent, opts?: ToastOptions) =>
      once(key, content, { ...opts, type: "info" }),
    [once]
  );
  const onceWarn = useCallback(
    (key: string, content: ToastContent, opts?: ToastOptions) =>
      once(key, content, { ...opts, type: "warning" }),
    [once]
  );

  /** ---------- utilidades ---------- */
  const dismissAll = useCallback(() => {
    createdIdsRef.current.forEach((id) => toast.dismiss(id));
    createdIdsRef.current = [];
    keyMapRef.current.clear();
  }, []);

  const dismissKey = useCallback((key: string) => {
    const k = hashKey(key);
    const id = keyMapRef.current.get(k);
    if (id) {
      toast.dismiss(id);
      keyMapRef.current.delete(k);
      createdIdsRef.current = createdIdsRef.current.filter((x) => x !== id);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (autoDismissOnUnmount) dismissAll();
    };
  }, [autoDismissOnUnmount, dismissAll]);

  return {
    // paralelos automáticos
    auto,
    autoSuccess,
    autoError,
    autoInfo,
    autoWarn,
    // deduplicados por chave
    once,
    onceSuccess,
    onceError,
    onceInfo,
    onceWarn,
    // util
    dismissAll,
    dismissKey
  };
}
